import React, { useState, useRef, useEffect } from 'react';
import { X, PaperPlaneTilt, ChatCircleDots, Sparkle } from '@phosphor-icons/react';
import { proxyImage } from '../lib/imageProxy';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_PROMPTS = [
  'अपनी सबसे प्रिय रचना के बारे में बताइए।',
  'आपकी लेखनी को किसने सबसे अधिक प्रभावित किया?',
  'आज के युवा लेखकों को क्या सलाह देंगे?',
  'What does poetry mean to you?',
];

/**
 * A slide-out drawer that lets the user chat with a Claude-powered persona of a legendary author.
 * Session-only — messages live in local React state and are not persisted server-side.
 */
export default function AuthorChatDrawer({ author, open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // Reset state when opening a new author
  useEffect(() => {
    if (open) {
      setMessages([]);
      setInput('');
    } else {
      // If drawer closes mid-stream, abort
      abortRef.current?.abort();
    }
  }, [open, author?.author_id]);

  const sendMessage = async (text) => {
    if (!text?.trim() || streaming || !author) return;
    const userMsg = { role: 'user', content: text.trim() };
    const nextMessages = [...messages, userMsg];
    setMessages([...nextMessages, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/api/ai/author-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ author_id: author.author_id, messages: nextMessages }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let assistantText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE frames: each event ends with a blank line
        const frames = buffer.split('\n\n');
        buffer = frames.pop(); // last (potentially incomplete) frame stays in buffer

        for (const frame of frames) {
          if (!frame.trim()) continue;
          // Handle "event: done" and "event: error"
          if (frame.startsWith('event: done')) {
            break;
          }
          if (frame.startsWith('event: error')) {
            const errPayload = frame.split('\n').find((l) => l.startsWith('data: '))?.slice(6) || 'Unknown error';
            throw new Error(errPayload);
          }
          // Data lines
          const dataLines = frame.split('\n').filter((l) => l.startsWith('data: '));
          for (const dl of dataLines) {
            const chunk = dl.slice(6).replace(/\\n/g, '\n');
            if (chunk === '[DONE]') continue;
            assistantText += chunk;
            setMessages((curr) => {
              const copy = curr.slice();
              copy[copy.length - 1] = { role: 'assistant', content: assistantText };
              return copy;
            });
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      toast.error(err.message?.slice(0, 140) || 'Chat error');
      // Remove the empty assistant placeholder on error
      setMessages((curr) => {
        const last = curr[curr.length - 1];
        if (last && last.role === 'assistant' && !last.content) {
          return curr.slice(0, -1);
        }
        return curr;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  if (!open || !author) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-background/70 backdrop-blur-sm z-40 animate-in fade-in duration-200"
        data-testid="author-chat-backdrop"
      />
      {/* Drawer */}
      <div
        className="fixed inset-y-0 right-0 w-full max-w-lg bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300"
        data-testid="author-chat-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full overflow-hidden bg-muted flex-shrink-0 border border-border">
              {author.photograph ? (
                <img src={proxyImage(author.photograph)} alt={author.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-primary">
                  <Sparkle size={20} weight="duotone" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm text-muted-foreground">Chatting with</div>
              <div className="font-serif text-lg font-semibold truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {author.name}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="author-chat-close"
            className="p-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Close chat"
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-4" data-testid="author-chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <ChatCircleDots size={30} weight="duotone" className="text-primary" />
              </div>
              <h3 className="text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
                Ask {author.name} anything
              </h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
                A conversation powered by AI — grounded in {author.name}'s life, work, and voice. Session-only, not saved.
              </p>
              <div className="grid gap-2 max-w-sm mx-auto">
                {SUGGESTED_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(p)}
                    data-testid={`author-chat-suggested-${i}`}
                    className="text-left text-sm px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted rounded-bl-sm'
                }`}
                data-testid={`author-chat-msg-${m.role}-${i}`}
                style={m.role === 'assistant' ? { fontFamily: "'Noto Sans Devanagari', 'IBM Plex Sans', sans-serif" } : {}}
              >
                {m.content}
                {m.role === 'assistant' && !m.content && streaming && (
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse"></span>
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage(input);
          }}
          className="border-t border-border px-4 py-3 bg-background/60"
        >
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(input);
                }
              }}
              placeholder={`Message ${author.name}…`}
              disabled={streaming}
              rows={1}
              data-testid="author-chat-input"
              className="flex-1 resize-none bg-muted/50 border border-transparent focus:border-primary focus:bg-background rounded-2xl px-4 py-3 text-sm outline-none max-h-32"
              style={{ fontFamily: "'Noto Sans Devanagari', 'IBM Plex Sans', sans-serif" }}
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="author-chat-send"
              className="p-3 bg-primary text-primary-foreground rounded-full hover:opacity-90 disabled:opacity-40 transition-opacity flex-shrink-0"
              aria-label="Send"
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 px-1">
            AI persona · session-only · not persisted · powered by Claude Sonnet 4.6
          </p>
        </form>
      </div>
    </>
  );
}
